import { useState } from "@faire/web--source/ui/hooks/useState";
import { useCallback, useEffect } from "react";

import { IInstantAnswerProps } from "../components/InstantAnswerCard";
import {
  InstantAnswer,
  InstantAnswerRequiredData,
  SelfServeContext,
} from "../types";

import { useAsyncDataStatus } from "./useAsyncDataStatus";
import { useSelfServeContext } from "./useSelfServeContext";

/**
 * Calls the async functions to fetch the additional data required by the instant answer;
 * those functions are defined in the additionalRequiredData field of the instant answer.
 */
export const useInstantAnswerContextAdditionalData = (
  instantAnswer: InstantAnswer | undefined
) => {
  const { context } = useSelfServeContext();
  const { contextAdditionalData, updateContextAdditionalData } =
    useContextAdditionalDataState();

  const {
    asyncDataInProgress,
    asyncDataComplete,
    setAsInProgress,
    setAsComplete,
  } = useAsyncDataStatus();

  useEffect(() => {
    if (asyncDataInProgress || asyncDataComplete) {
      return;
    }

    if (!instantAnswer || !instantAnswer.additionalRequiredData) {
      setAsComplete();
      return;
    }

    setAsInProgress();
    invokeAdditionalDataFns(instantAnswer, context)
      .then(updateContextAdditionalData)
      .then(setAsComplete);
  }, [
    asyncDataComplete,
    asyncDataInProgress,
    setAsComplete,
    setAsInProgress,
    instantAnswer,
    context,
    updateContextAdditionalData,
  ]);

  return {
    instantAnswerContextAdditionalData: contextAdditionalData,
    asyncDataComplete,
  };
};

const useContextAdditionalDataState = () => {
  const [contextAdditionalData, setContextAdditionalData] = useState<
    Partial<IInstantAnswerProps>
  >({});

  const updateContextAdditionalData = useCallback(
    (responses: InstantAnswerRequiredData[]) => {
      setContextAdditionalData(() => collectAdditionalData(responses));
    },
    []
  );

  return {
    contextAdditionalData,
    updateContextAdditionalData,
  };
};

const collectAdditionalData = (
  responses: InstantAnswerRequiredData[]
): Readonly<Partial<IInstantAnswerProps>> => {
  return responses.reduce((additionalData, { field, data }) => {
    return {
      ...additionalData,
      [field]: data,
    };
  }, {});
};

const invokeAdditionalDataFns = (
  instantAnswer: InstantAnswer,
  context: SelfServeContext
): Promise<InstantAnswerRequiredData[]> => {
  if (!instantAnswer.additionalRequiredData) {
    return Promise.resolve([]);
  }

  const allPromises = instantAnswer.additionalRequiredData.map(
    (dataFetcherFn) => dataFetcherFn(context)
  );
  return Promise.all(allPromises);
};
